/**
 * DiagramCard Component
 *
 * One diagram in the thumbnail grid. It lazy-loads its thumbnail via
 * `useThumbnail` (fetched only once the card scrolls into view, cached by
 * Diagram_Id and Version, object URL revoked on unmount), and shows the
 * Diagram_Title, Diagram_Folder, last editor, relative last-edited time (with
 * the exact time in a tooltip), and an "Editing: {name}" badge when someone
 * holds the Edit_Lock. See Requirements 9.3 and 9.6.
 *
 * The New diagram dialog and the actions menu are separate tasks; this card
 * exposes an `actionsSlot` for the actions menu and leaves opening the diagram
 * to the parent via `onOpen`.
 */

import { formatDistanceToNow } from "date-fns"
import { ImageOff, Loader2, Pencil } from "lucide-react"
import type { DiagramSummary } from "@/services/diagramsService"
import { useThumbnail } from "@/components/diagrams/useThumbnail"
import DiagramKindBadge from "@/components/diagrams/DiagramKindBadge"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { formatDate } from "@/utils/dateUtils"

/** Props for the DiagramCard component. */
interface DiagramCardProps {
  /** The diagram to display. */
  diagram: DiagramSummary
  /** Returns a fresh Auth0 access token for the thumbnail request. */
  getAccessToken: () => Promise<string>
  /** Called when the user activates the card to open the diagram in the editor. */
  onOpen: (diagram: DiagramSummary) => void
  /**
   * The actions menu for this diagram (task 5.4), rendered in the card's corner.
   * Optional so the card renders before the actions task lands.
   */
  actionsSlot?: React.ReactNode
}

/**
 * A single diagram card for the grid view.
 *
 * @param props - Component props.
 * @param props.diagram - The diagram to display.
 * @param props.getAccessToken - Token getter for the lazy thumbnail fetch.
 * @param props.onOpen - Handler to open the diagram in the editor.
 * @param props.actionsSlot - Optional actions menu rendered in the corner.
 * @returns The rendered card element.
 */
export default function DiagramCard({
  diagram,
  getAccessToken,
  onOpen,
  actionsSlot,
}: DiagramCardProps): React.JSX.Element {
  const { ref, state } = useThumbnail({
    id: diagram.id,
    version: diagram.version,
    getAccessToken,
  })

  const relativeEdited: string = formatDistanceToNow(new Date(diagram.updatedAt), {
    addSuffix: true,
  })
  const exactEdited: string = formatDate(diagram.updatedAt)

  return (
    <div
      ref={ref}
      className="group relative flex flex-col overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-primary focus-within:border-primary"
    >
      {/* Thumbnail area, also the open trigger. A button keeps it keyboard
          accessible; the figure inside is purely presentational. Always white
          like the diagrams themselves; written as `#ffffff` because
          globals.css remaps `.bg-white` to the theme background. */}
      <button
        type="button"
        onClick={() => onOpen(diagram)}
        aria-label={`Open ${diagram.title}`}
        className="flex aspect-[4/3] w-full items-center justify-center bg-[#ffffff] outline-none"
      >
        {state.isLoading && (
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden="true" />
        )}
        {!state.isLoading && state.objectUrl !== null && (
          <img
            src={state.objectUrl}
            alt={`Thumbnail of ${diagram.title}`}
            className="h-full w-full object-contain p-2"
          />
        )}
        {!state.isLoading && state.objectUrl === null && (
          <span className="flex flex-col items-center gap-1 text-muted-foreground">
            <ImageOff className="size-5" aria-hidden="true" />
            <span className="text-xs">
              {state.error !== null ? "Preview unavailable" : "No preview"}
            </span>
          </span>
        )}
      </button>

      {/* Actions menu (task 5.4), pinned to the top-right corner. */}
      {actionsSlot !== undefined && (
        <div className="absolute right-1.5 top-1.5">{actionsSlot}</div>
      )}

      <div className="flex flex-col gap-1 border-t border-border p-2">
        <TruncatedCell value={diagram.title} className="text-sm font-medium" />
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <div className="flex min-w-0 items-center gap-1.5">
            <DiagramKindBadge kind={diagram.kind} className="shrink-0" />
            <TruncatedCell value={diagram.folder ?? "Unfiled"} className="min-w-0" />
          </div>
          {diagram.lock !== null && (
            <Badge variant="secondary" className="shrink-0 gap-1">
              <Pencil className="size-3" aria-hidden="true" />
              <span className="max-w-24 truncate">Editing: {diagram.lock.holderName}</span>
            </Badge>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <TruncatedCell value={diagram.updatedByName} className="min-w-0" />
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0 cursor-default">{relativeEdited}</span>
            </TooltipTrigger>
            <TooltipContent>{exactEdited}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
